import AddToCartFlyout from './AddToCartFlyout';
import Forms from '../Forms';
import ProductGallery from './ProductGallery';
import VariantHelper from '../VariantHelper';

import { numberInputValidation, removeTrapFocus, trapFocus } from '#helpers';

export default class ProductDetails {
  constructor(options) {
    this.$window = $(window);
    this.$formArea = options.$formArea;
    this.$gallery = options.$gallery;
    this.$details = options.$details;
    this.context = options.context;
    this.settings = options.settings;
    this.volumeEquivalencies = options.volumeEquivalencies;
    this.variantAvailabilities = options.variantAvailabilities;
    this.product = options.product;
    this.badges = options.badges;
    this.cdfData = options.cdfData;
    this.useHistory = options.useHistory;

    this.variantHelper = null;

    // Product ATC Actions
    this._onATCOpen = this._onATCOpen.bind(this);
    this._onATCClose = this._onATCClose.bind(this);

    this.atcCallbacks = $.extend(
      {
        onClose: this._onATCClose,
        onOpen: this._onATCOpen
      },
      options.atcCallbacks
    );

    // Form
    this.$form = this.$formArea.find('[data-product-form] form');
    this.$productAtcButton = this.$formArea.find('[data-product-atc]');
    this.$productVariants = this.$form.find('[data-variants]');
    this.$productOptions = this.$form.find('[data-product-option]');

    this.variantFields = {
      $price: this.$details.find('[data-price]'),
      $priceMoney: this.$details.find('[data-price] .money'),
      $compareAtPrice: this.$details.find('[data-price-compare-at]'),
      $compareAtPriceMoney: this.$details.find(
        '[data-price-compare-at] .money'
      ),
      $badge: this.$details.find('[data-product-badge]'),
      $badgeMoneySaved: this.$details.find('[data-price-money-saved]'),
      $badgePercentSaved: this.$details.find('[data-price-percent-saved]'),
      $sku: this.$formArea.find('[data-product-sku]'),
      $volumeEquivalency: this.$formArea.find(
        '[data-product-volume-equivalency]'
      ),
      $dollarPerGram: this.$details.find('[data-dollar-per-gram]'),
      $formSku: this.$formArea.find('[data-product-form-sku]'),
      $formCdf: this.$formArea.find('[data-product-form-cdf]'),
      $formConsumerUnits: this.$formArea.find(
        '[data-product-form-consumer-units]'
      )
    };

    this.forms = new Forms(this.$form);
    this.gallery = new ProductGallery(options);

    this._bindEvents();
    this._initOptions();
  }

  unload() {
    this.$form.off('.product-details');
    this.forms.unload();

    if (this.variantHelper) {
      this.variantHelper.unload();
    }

    this.gallery.unload();
  }

  _bindEvents() {
    this.$window.on('product-variant-switch.product-details', (event, data) =>
      this._switchVariant(event, data)
    );
    this.$form.on('submit.product-details', event =>
      this._AddToCartFlyout(event)
    );
    this.$form.on(
      'keydown.product-details',
      '[data-productitem-quantity]',
      event => numberInputValidation(event)
    );
  }

  _initOptions() {
    if (!this.$productVariants.length) {
      return;
    }

    this.variantHelper = new VariantHelper(
      this.product,
      this.variantAvailabilities,
      this.$productVariants,
      this.$productOptions
    );
  }

  _switchVariant(event, data) {
    if (data.product !== this.product) {
      return;
    }

    const variant = data.variant;
    const firstLoad = data.firstLoad;

    if (firstLoad) {
      return;
    }

    // Update main select
    this.$productVariants.val(data.variant.id);
    this.gallery.variantChange(variant);

    // Update Variant information
    this._updatePrice(variant);
    this._updateBadge(variant);
    this._updateButton(variant);
    this._updateSku(variant);
    this._updateCdf(variant);
    this._updateVolumeEquivalency(variant);
    this._updateDollarPerGram(variant);

    if (this.useHistory) {
      const url = `${this.product.handle}?${$.param({ variant: variant.id })}`;
      history.replaceState({}, 'variant', url);
    }
  }

  _updatePrice(variant) {
    // Update compare at price
    const hasComparePrice = this._checkForOnSale(variant);
    this.variantFields.$compareAtPrice.toggleClass('visible', hasComparePrice);
    this.variantFields.$compareAtPriceMoney.text(
      Shopify.formatMoney(variant.compare_at_price, this.settings.money_format)
    );

    // Update price
    this.variantFields.$priceMoney.text(
      Shopify.formatMoney(variant.price, this.settings.money_format)
    );
  }

  _updateBadge(variant) {
    const $badge = $(this.badges[variant.id]);
    this.variantFields.$badge.html($badge);
  }

  _updateButton(variant) {
    const availability_object = this.variantAvailabilities[variant.id];

    if (availability_object.online_eligible == 'no') {
      this.$productAtcButton.text(this.context.product_not_available);
      this.$productAtcButton.addClass('disabled').prop('disabled', true);
    } else if (availability_object.wholesale_lifecycle == 'temporary_hold') {
      this.$productAtcButton.text(this.context.product_not_in_stock);
      this.$productAtcButton.addClass('disabled').prop('disabled', true);
    } else if (!availability_object.available) {
      this.$productAtcButton.text(this.context.product_unavailable);
      this.$productAtcButton.addClass('disabled').prop('disabled', true);
    } else {
      this.$productAtcButton.text(this.context.product_available);
      this.$productAtcButton.removeClass('disabled').prop('disabled', false);
    }
  }

  _updateSku(variant) {
    if (!variant.sku) {
      this.variantFields.$sku.empty();
      this.variantFields.$formSku.val('');
      return false;
    }

    const text = this.context.sku_value.replace('** sku **', variant.sku);
    this.variantFields.$sku.text(text);
    this.variantFields.$formSku.val(variant.sku);
  }

  _updateCdf(variant) {
    const { sku } = variant;
    if (!this.cdfData[sku]) {
      this.variantFields.$formCdf.val('');
      return false;
    }

    this.variantFields.$formCdf.val(this.cdfData[sku].cdf_sku);
    this.variantFields.$formConsumerUnits.val(this.cdfData[sku].consumer_units);
  }

  _updateVolumeEquivalency(variant) {
    const volumeEquivalency = this._getVolumeEquivalency(
      variant,
      this.variantFields.$volumeEquivalency
    );

    if (!volumeEquivalency) {
      return false;
    }

    const text = this.context.volume_equivalency.replace(
      '** quantity **',
      volumeEquivalency
    );
    this.variantFields.$volumeEquivalency.text(text);
  }

  // Updates the dollar $/g messaging on variant switch
  _updateDollarPerGram(variant) {
    const $dollarPerGramEl = this.variantFields.$dollarPerGram;
    const volumeEquivalency = this._getVolumeEquivalency(
      variant,
      $dollarPerGramEl
    );

    if (!volumeEquivalency) {
      return false;
    }

    const displayDollarPerGram = Shopify.formatMoney(
      variant.price / volumeEquivalency,
      this.settings.money_format
    );
    const checkIfOnSale = this._checkForOnSale(variant);
    const saleClass = 'product-dollar-per-gram--sale';
    const uom = 'g';

    $dollarPerGramEl
      .toggleClass(saleClass, checkIfOnSale)
      .text(`(${displayDollarPerGram}/${uom})`);
  }

  _getVolumeEquivalency(variant, $element) {
    if (!$element.length) {
      return false;
    }

    if (!variant.sku || !this.volumeEquivalencies[variant.sku]) {
      // Clear the element for variant switch
      $element.empty();
      return false;
    }

    return this.volumeEquivalencies[variant.sku];
  }

  _checkForOnSale(variant) {
    return (
      !!variant.compare_at_price && variant.compare_at_price > variant.price
    );
  }

  _AddToCartFlyout(event) {
    event.preventDefault();

    const formData = this.$form.serializeArray();

    const options = {
      $atcButton: this.$productAtcButton,
      settings: {
        moneyFormat: this.settings.money_format
      },
      availabilities: this.variantAvailabilities
    };

    AddToCartFlyout.init(formData, options, this.atcCallbacks);
  }

  /**
   * Callback when AddToCartFlyout opens
   * Move keyboard focus into flyout
   *
   * @param {object} options - Options to be used
   * @param {jQuery} options.$container - Container to trap focus within
   * @param {jQuery} options.$elementToFocus - Element to be focused when focus leaves container
   * @private
   */
  _onATCOpen(options) {
    trapFocus({
      $container: options.$container,
      $elementToFocus: options.$elementToFocus,
      namespace: 'product-details-flyout'
    });
  }

  /**
   * Callback when AddToCartFlyout closes
   * Move keyboard focus back into grid item
   *
   * @private
   */
  _onATCClose() {
    removeTrapFocus({
      namespace: 'product-details-flyout'
    });

    this.$productAtcButton.focus();
  }
}
